import { useEffect, useState } from 'react';
import { adminApi, ApiError, type Overview, type OverviewNow, type OverviewSample } from '../api';
import { useApp } from '../context';
import { linkProps } from '../router';

/**
 * Overzicht voor de beheerder: wie kijkt er nu, gezondheid, server, activiteit en beste rijen.
 * Ververst elke 15 seconden zolang de pagina open is.
 */

const KIND: Record<OverviewNow['kind'], string> = { plex: 'Plex', addon: 'Addon', live: 'Live TV' };

function gb(n: number): string {
  return `${(n / 1024 ** 3).toFixed(n >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
}

function uptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d ? `${d} ${d === 1 ? 'dag' : 'dagen'} aan` : `${h} uur aan`;
}

function shortDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export function AdminOverview() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => adminApi.overview()
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e: unknown) => live && setError(e instanceof ApiError && e.status === 403 ? 'Alleen de beheerder kan het overzicht bekijken.' : 'Overzicht ophalen mislukt.'));
    void load();
    const t = window.setInterval(load, 15_000);
    return () => {
      live = false;
      window.clearInterval(t);
    };
  }, []);

  if (error && !data) return <p className="placeholder-row error" role="alert">{error}</p>;
  if (!data) return <p className="placeholder-row" aria-busy="true">Alles nalopen…</p>;

  const issues = data.health.filter((h) => h.state === 'bad' || h.state === 'warn');
  const change = data.totals.playsPrev30 > 0 ? Math.round(((data.totals.plays30 - data.totals.playsPrev30) / data.totals.playsPrev30) * 100) : null;

  return (
    <div className="ov">
      <p className="subtle ov-intro">tumedia {data.version} · {uptime(data.uptimeSec)} · <a {...linkProps('/settings/status')}>Statusdetails</a></p>

      <section className="ov-tiles" aria-label="Kerncijfers">
        <Tile label="Nu aan het kijken" value={String(data.now.length)} sub={data.now.length ? summarizeKinds(data.now) : 'Niemand'} accent={data.now.length > 0} />
        <Tile label="Kijkers (30 dagen)" value={String(data.totals.viewers30)} sub="Plex-accounts en profielen" />
        <Tile label="Afgespeeld (30 dagen)" value={String(data.totals.plays30)} sub={change === null ? 'Nog geen vorige maand om mee te vergelijken' : `${change >= 0 ? '+' : ''}${change}% t.o.v. de 30 dagen ervoor`} />
        <Tile label="Gezondheid" value={issues.length ? `${issues.length} ${issues.length === 1 ? 'melding' : 'meldingen'}` : 'Alles in orde'} sub={issues[0] ? `${issues[0].name}: ${issues[0].detail}` : `${data.health.length} onderdelen gecontroleerd`} accent={issues.length > 0} />
      </section>

      <div className="ov-row">
        <section className="ov-card ov-wide" aria-label="Nu aan het kijken">
          <div className="ov-head">
            <h2>Nu aan het kijken</h2>
            <span className="subtle">Plex, addons en Live TV samen</span>
          </div>
          {data.nowError && <p className="subtle">Plex: {data.nowError}</p>}
          {data.now.length === 0 && <p className="subtle">Er wordt nu niets afgespeeld.</p>}
          <div className="ov-now">
            {data.now.map((n, i) => <NowRow key={i} n={n} />)}
          </div>
        </section>

        <section className="ov-card" aria-label="Gezondheid">
          <div className="ov-head">
            <h2>Gezondheid</h2>
          </div>
          <ul className="ov-health">
            {data.health.map((h) => (
              <li key={h.name}>
                <span className={`status-dot ${h.state}`} aria-hidden="true" />
                <span className="ov-health-name">{h.name}</span>
                <span className={`ov-health-detail ${h.state === 'warn' || h.state === 'bad' ? 'is-warn' : ''}`}>{h.detail}</span>
                <span className="visually-hidden">{h.state === 'ok' ? 'in orde' : h.state === 'off' ? 'niet ingesteld' : h.state === 'warn' ? 'let op' : 'werkt niet'}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="ov-row">
        <section className="ov-card ov-wide" aria-label="Activiteit">
          <div className="ov-head">
            <h2>Activiteit, laatste 30 dagen</h2>
            <span className="ov-legend">
              <span><i className="ov-sw ov-plex" />Plex</span>
              <span><i className="ov-sw ov-addon" />Addons</span>
              <span><i className="ov-sw ov-live" />Live TV</span>
            </span>
          </div>
          <ActivityChart days={data.activity} />
          {!data.plexHistory && <p className="subtle hint">De Plex-geschiedenis kon niet worden opgehaald; alleen addons en Live TV worden geteld.</p>}
        </section>

        <section className="ov-card" aria-label="Server">
          <div className="ov-head">
            <h2>Server</h2>
            <span className="subtle">{data.server.cores} {data.server.cores === 1 ? 'kern' : 'kernen'}</span>
          </div>
          <ServerStats s={data.server} />
        </section>
      </div>

      <div className="ov-row">
        <section className="ov-card" aria-label="Beste rijen">
          <div className="ov-head">
            <h2>Beste rijen op Home</h2>
            <span className="subtle">titels geopend vanuit de rij, 30 dagen</span>
          </div>
          {data.rows.length === 0 ? (
            <p className="subtle">Nog geen gegevens. Dit wordt vanaf nu bijgehouden.</p>
          ) : (
            <ol className="ov-bars">
              {data.rows.map((r) => (
                <li key={r.title}>
                  <span className="ov-bar-name">{r.title}</span>
                  <span className="ov-bar" aria-hidden="true"><span style={{ width: `${Math.max(4, Math.round((r.n / data.rows[0].n) * 100))}%` }} /></span>
                  <strong>{r.n}</strong>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="ov-card" aria-label="Hoe er wordt gekeken">
          <div className="ov-head">
            <h2>Hoe er wordt gekeken</h2>
            <span className="subtle">30 dagen</span>
          </div>
          <Split split={data.split} />
          <p className="subtle hint">Addons via debrid spelen rechtstreeks via de internetverbinding van de kijker; Live TV en omzetten in de browser gaan via deze server.</p>
        </section>
      </div>
    </div>
  );
}

function summarizeKinds(now: OverviewNow[]): string {
  const c = { plex: 0, addon: 0, live: 0 };
  for (const n of now) c[n.kind]++;
  return (Object.keys(c) as OverviewNow['kind'][]).filter((k) => c[k]).map((k) => `${c[k]} ${KIND[k]}`).join(' · ');
}

function Tile({ label, value, sub, accent }: { label: string; value: string; sub: string; accent?: boolean }) {
  return (
    <div className="ov-tile">
      <span className="subtle">{label}</span>
      <strong className={accent ? 'is-accent' : undefined}>{value}</strong>
      <span className="subtle ov-tile-sub">{sub}</span>
    </div>
  );
}

function NowRow({ n }: { n: OverviewNow }) {
  const { plex } = useApp();
  const img = n.plexThumb ? plex.image(n.plexThumb, 92, 136) : n.image;
  return (
    <div className="ov-now-row">
      <span className={`ov-now-art ${n.kind === 'live' ? 'is-logo' : ''}`} style={img ? { backgroundImage: `url("${img}")` } : undefined} aria-hidden="true" />
      <span className="ov-now-body">
        <span className="ov-now-top">
          <strong>{n.title}</strong>
          <span className={`ov-kind ov-kind-${n.kind}`}>{KIND[n.kind]}</span>
          {n.paused && <span className="subtle">gepauzeerd</span>}
        </span>
        {n.sub && <span className="subtle">{n.sub}</span>}
        <span className="subtle">{n.who} · {n.device} · {n.mode}</span>
        {n.progress !== null && <span className="ov-progress" aria-hidden="true"><span style={{ width: `${n.progress}%` }} /></span>}
      </span>
      {n.quality && <span className="ov-now-q">{n.quality}</span>}
    </div>
  );
}

function ActivityChart({ days }: { days: Overview['activity'] }) {
  const max = Math.max(1, ...days.map((d) => d.plex + d.addon + d.live));
  const total = days.reduce((n, d) => n + d.plex + d.addon + d.live, 0);
  const marks = [0, 10, 20, days.length - 1];
  return (
    <>
      <div className="ov-chart" role="img" aria-label={`Afspeelacties per dag, laatste 30 dagen: ${total} in totaal, hoogste dag ${max}`}>
        {days.map((d) => (
          <span key={d.day} className="ov-col" title={`${shortDay(d.day)}: Plex ${d.plex}, addons ${d.addon}, Live TV ${d.live}`}>
            <span className="ov-live" style={{ height: `${(d.live / max) * 100}%` }} />
            <span className="ov-addon" style={{ height: `${(d.addon / max) * 100}%` }} />
            <span className="ov-plex" style={{ height: `${(d.plex / max) * 100}%` }} />
          </span>
        ))}
      </div>
      <div className="ov-axis" aria-hidden="true">
        {marks.map((i) => <span key={i}>{i === days.length - 1 ? 'vandaag' : shortDay(days[i].day)}</span>)}
      </div>
    </>
  );
}

function Spark({ values, max, cls }: { values: (number | null)[]; max: number; cls: string }) {
  return (
    <span className={`ov-spark ${cls}`} aria-hidden="true">
      {values.map((v, i) => <span key={i} style={{ height: `${v == null ? 0 : Math.max(4, Math.min(100, (v / Math.max(max, 0.001)) * 100))}%` }} />)}
    </span>
  );
}

function ServerStats({ s }: { s: Overview['server'] }) {
  const last: OverviewSample | undefined = s.samples[s.samples.length - 1];
  const out = s.samples.map((x) => x.txMbps);
  const inn = s.samples.map((x) => x.rxMbps);
  const netMax = Math.max(1, ...out.map((v) => v ?? 0), ...inn.map((v) => v ?? 0));
  const mbit = (v: number | null | undefined) => (v == null ? '…' : `${v < 10 ? v.toFixed(1) : Math.round(v)} Mbit/s`);
  return (
    <div className="ov-server">
      <div className="ov-metric">
        <span className="ov-metric-top"><span>CPU</span><strong>{last?.cpu == null ? 'meten…' : `${Math.round(last.cpu)}%`}</strong></span>
        <Spark values={s.samples.map((x) => x.cpu)} max={100} cls="ov-c1" />
      </div>
      <div className="ov-metric">
        <span className="ov-metric-top"><span>Geheugen</span><strong>{gb(s.memory.used)} van {gb(s.memory.total)}</strong></span>
        <Spark values={s.samples.map((x) => x.memUsed)} max={s.memory.total} cls="ov-c2" />
      </div>
      <div className="ov-metric">
        <span className="ov-metric-top"><span>Netwerk uit</span><strong>{mbit(last?.txMbps)}</strong></span>
        <Spark values={out} max={netMax} cls="ov-c3" />
      </div>
      <div className="ov-metric">
        <span className="ov-metric-top"><span>Netwerk in</span><strong>{mbit(last?.rxMbps)}</strong></span>
        <Spark values={inn} max={netMax} cls="ov-c4" />
      </div>
      {s.disk && (
        <div className="ov-metric">
          <span className="ov-metric-top"><span>Opslag (appdata)</span><strong>{gb(s.disk.total - s.disk.free)} van {gb(s.disk.total)}</strong></span>
          <span className="ov-progress" aria-hidden="true"><span style={{ width: `${Math.round(((s.disk.total - s.disk.free) / s.disk.total) * 100)}%` }} /></span>
        </div>
      )}
      <p className="subtle hint">Laatste 10 minuten; er wordt alleen gemeten zolang deze pagina open is.</p>
    </div>
  );
}

function Split({ split }: { split: Overview['split'] }) {
  const total = split.plex + split.addon + split.live;
  const parts: { key: keyof Overview['split']; label: string }[] = [
    { key: 'plex', label: 'Plex' },
    { key: 'addon', label: 'Addons' },
    { key: 'live', label: 'Live TV' },
  ];
  if (!total) return <p className="subtle">Nog geen afspeelacties in de laatste 30 dagen.</p>;
  return (
    <>
      <div className="ov-split" aria-hidden="true">
        {parts.map((p) => split[p.key] > 0 && <span key={p.key} className={`ov-${p.key}`} style={{ flexGrow: split[p.key] }} />)}
      </div>
      <div className="ov-split-cards">
        {parts.map((p) => (
          <div key={p.key} className="ov-split-card">
            <span><i className={`ov-sw ov-${p.key}`} aria-hidden="true" />{p.label}</span>
            <strong>{Math.round((split[p.key] / total) * 100)}%</strong>
            <span className="subtle">{split[p.key]} keer</span>
          </div>
        ))}
      </div>
    </>
  );
}
